import React, { useState, useEffect } from "react";
import { useIsAuthenticated, useMsal } from "@azure/msal-react";
import { Box, Container, Skeleton } from "@mui/material";

import { loginRequest } from "../../services/authConfig";
import { callMsGraph } from "../../services/graph";
import CustomerService from "../../services/customerService";
import ProfileTop from "./ProfileTop";

const fmtEur = (v) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "EUR" }).format(
    Number(v) || 0
  );

export const TopBar = () => {
  const isAuthenticated = useIsAuthenticated();
  const { instance, accounts } = useMsal();

  const [graphData, setGraphData] = useState(null);
  const [customerData, setCustomerData] = useState(null);
  const [customerLoaded, setCustomerLoaded] = useState(false);

  useEffect(() => {
    if (!isAuthenticated || accounts.length === 0) return;

    let cancelled = false;

    instance
      .acquireTokenSilent({ ...loginRequest, account: accounts[0] })
      .then((response) =>
        callMsGraph(response.accessToken).then((resp) => {
          if (!cancelled) setGraphData(resp);
        })
      )
      .catch((e) => console.warn("TopBar :: Graph token failed", e));

    const customerService = CustomerService();
    customerService
      .getCustomer()
      .then((response) => {
        if (!cancelled) setCustomerData(response);
      })
      .catch((e) => console.warn("TopBar :: getCustomer failed", e))
      .finally(() => {
        if (!cancelled) setCustomerLoaded(true);
      });

    return () => {
      cancelled = true;
    };
  }, [accounts, isAuthenticated, instance]);

  if (!isAuthenticated) return null;

  // Loading skeleton — only show until we know whether the customer fetch
  // succeeded. Avoids the banner blink-out the user noticed.
  if (!customerLoaded) {
    return (
      <Container maxWidth="xl" sx={{ py: 1.5 }}>
        <Box
          sx={{
            display: "flex",
            gap: 2,
            alignItems: "center",
            flexWrap: "wrap",
          }}
        >
          <Skeleton variant="rounded" width={220} height={28} />
          <Skeleton variant="rounded" width={140} height={28} />
          <Skeleton variant="rounded" width={140} height={28} />
          <Skeleton variant="rounded" width={140} height={28} />
          <Skeleton variant="rounded" width={140} height={28} />
        </Box>
      </Container>
    );
  }

  // Even if customer data is partially missing (BC NullRef earlier in the
  // session), keep showing the banner with the graph info + empty placeholders
  // so the page doesn't lose its top section.
  const customerName = customerData?.name || (graphData ? "Account" : "");
  const userName = graphData
    ? `${graphData.givenName || ""} ${graphData.surname || ""}`.trim()
    : accounts[0]?.name || "";

  return (
    <ProfileTop
      userName={userName}
      customerName={customerName}
      balanceLCY={fmtEur(customerData?.balanceLCY)}
      balanceDueLCY={fmtEur(customerData?.balanceDueLCY)}
      creditLimitLCY={fmtEur(customerData?.creditLimitLCY)}
      outstandingOrdersLCY={fmtEur(customerData?.outstandingOrdersLCY)}
    />
  );
};
